<script lang="ts" setup>
import { ref } from 'vue';
import { TechDataVehicle } from '../types';
import { getDefaultLocaleValue } from '../locales/default';
import { useTracking } from '@/composables/useTracking';
import { useIntersectionObserverOnce } from '@/composables/useIntersectionObserverOnce';

const props = defineProps<{
  vehicle: TechDataVehicle;
  translations: Record<string, string>;
  modelSeries: string;
}>();

const sliderContainer = ref<HTMLElement | null>(null);
const isVisible = ref<boolean>(false);

const getAltText = (vehicleClass: string, bodyType: string, key: string) => {
  let bodyTypeTranslated =
    props.translations[`owvc_tech_data_bodytype_${bodyType}`];
  let dimensions = props.translations[`owvc_tech_data_alt_image_${key}`];

  if (bodyTypeTranslated && dimensions) {
    return `Mercedes-Benz ${vehicleClass} ${bodyTypeTranslated.toLowerCase()}: ${dimensions}`;
  } else {
    return `Mercedes-Benz ${vehicleClass} ${bodyType}: ${getDefaultLocaleValue(
      `image_alt_${key}`,
    )}`;
  }
};

const { trackSlider } = useTracking(props.modelSeries);

useIntersectionObserverOnce(
  sliderContainer,
  {
    rootMargin: '200px 0px',
    threshold: 0,
  },
  () => {
    isVisible.value = true;
  },
);
</script>

<template>
  <div class="owvans-tech-data__slider" ref="sliderContainer">
    <div :class="{ 'is-hidden': isVisible }" class="slider-ssr">
      <button class="slider-ssr__arrow-button">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
          <path
            d="M5.35156 20.7953L16.3828 9.76252L18.5906 11.9688L7.55781 23L5.35156 20.7953Z"
          ></path>
          <path
            d="M5.41394 3.20469L7.61863 1L18.6514 12.0328L16.4452 14.239L5.41394 3.20469Z"
          ></path>
        </svg>
      </button>
      <img
        class="slider-ssr__image"
        :alt="getAltText(vehicle.vehicleClass, vehicle.bodyType, 'side')"
        :src="props.vehicle.imagesData.side"
      />
      <div class="slider-ssr__thumb-container">
        <div
          class="slider-ssr__thumb"
          v-for="(variation, key) in props.vehicle.imagesData"
          :key="key"
        >
          <img
            :alt="getAltText(vehicle.vehicleClass, vehicle.bodyType, key)"
            :src="variation"
          />
        </div>
      </div>
    </div>

    <wb7-gallery
      v-if="isVisible"
      preventFullscreenMode
      @wbgallerynextclick="trackSlider('vehicle_view.arrow', props.vehicle)"
      @wbgallerypreviousclick="trackSlider('vehicle_view.arrow', props.vehicle)"
      @wbgallerythumbclick="trackSlider('vehicle_view.image', props.vehicle)"
    >
      <wb7-gallery-item
        v-for="(variation, key) in props.vehicle.imagesData"
        :key="variation"
        :src="variation"
        :alt="getAltText(vehicle.vehicleClass, vehicle.bodyType, key)"
      />
    </wb7-gallery>
  </div>
</template>

<style scoped>
.slider-ssr {
  position: relative;
}

.is-hidden {
  visibility: hidden;
}

.wb-gallery {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
}

.slider-ssr__arrow-button {
  position: absolute;
  top: 50%;
  right: 0;
  width: 48px;
  height: 48px;
  margin-right: 16px;
  transform: translateY(-50%);
  background-color: transparent;
  border: var(--round-button-border-weight) solid
    var(--round-button-border-color);
  box-shadow:
    0 0 0.25rem 0 rgba(0, 0, 0, 0.1),
    0 1rem 1.5rem 0 rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  cursor: pointer;
}

.slider-ssr__arrow-button svg {
  width: 16px;
}

.slider-ssr__image {
  width: 100%;
}

.slider-ssr__thumb-container {
  height: 4.5rem;
  display: flex;
  gap: 16px;
}

.slider-ssr__thumb img {
  height: 100%;
}

.slider-ssr__thumb:first-child {
  border: 1px solid black;
}

.slider-ssr__thumb:not(:first-child) {
  position: relative;
}

.slider-ssr__thumb:not(:first-child)::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 50%;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0));
  pointer-events: none;
}

@media (max-width: 767px) {
  .slider-ssr__thumb-container {
    height: 2.625rem;
  }

  .slider-ssr__thumb img {
    height: 2.625rem;
  }
}
</style>
